import videojs from "video.js";
import { findLast } from "./find-last.js";

const Component = videojs.getComponent('Component');

class TimelinePreviewImage extends Component {
	constructor(player, options) {
		super(player, options);

		this.images = [];

		const { type } = options;
		if (type === 'images') {
			this.thumbnailWidth = 200;
			this.thumbnailHeight = 112;
			this.initOfImages();
		} else if (this.options_.url && (type === 'sprite' || type === 'sprite_with_times')) {
			this.computeImageSize();
			this.initOfSprite();
		}

		player.on('eventsOnTimeline/eventsRendered', () => {
			this.initOfTimelineEvents();
		});

		player.on('playerresize', () => {
			if (player.currentWidth() <= (options.breakpoint || 1024)) {
				this.hide();
				this.player().addClass('vjs-timeline-preview_hide');
			} else {
				this.show();
				this.player().removeClass('vjs-timeline-preview_hide');
			}
		});
	}

	set thumbnailWidth(value) {
		this._thumbnailWidth = value;
		this.el().style.setProperty('--width', `${this.thumbnailWidth}px`);
	}

	get thumbnailWidth() {
		return this._thumbnailWidth;
	}

	set thumbnailHeight(value) {
		this._thumbnailHeight = value;
		this.el().style.setProperty('--height', `${this.thumbnailHeight}px`);
	}

	get thumbnailHeight() {
		return this._thumbnailHeight;
	}

	computeImageSize() {
		const image = document.createElement('img');
		image.onload = () => {
			const { spriteRows, spriteColumns } = this.getSpriteSizes();
			this.thumbnailWidth = image.width / spriteColumns;
			this.thumbnailHeight = image.height / spriteRows;
		};
		image.src = this.options_.url;
	}

	getSpriteSizes() {
		const [ spriteRows, spriteColumns ] = this.options_.sprite_size.split('x');
		return { spriteRows, spriteColumns };
	}

	initOfSprite() {
		this.addClass('vjs-timeline-preview__thumbnail_vod');
		this.el().style.setProperty('--background-image', `url('${this.options_.url}')`);
		const { spriteRows, spriteColumns } = this.getSpriteSizes();
		this.options_.progressControl.el().addEventListener(
			'mousemove',
			() => this.showThumbnailOfSprite({ spriteRows, spriteColumns })
		);
	}

	showThumbnailOfSprite({ spriteRows, spriteColumns }, thumbnailTime) {
		// setTimeout нужен чтобы получить актуальное значение this.options_.timeTooltip.el().innerText из DOM
		setTimeout(() => {
			const stepInSeconds = this.player().duration() / this.options_.thumbs_count;
			const [
				cursorSeconds = '0',
				cursorMinutes = '0',
				cursorHours = '0'
			] = this.options_.timeTooltip.el().innerText.split(':').reverse().map((val) => Number.parseInt(val, 10));
			const cursorTime = thumbnailTime ? thumbnailTime : (cursorHours * 3600 + cursorMinutes * 60 + cursorSeconds);

			let thumbNumber = 0;
			const times = this.options_.times;
			if (Array.isArray(times)) {
				thumbNumber = times.findIndex(t => cursorTime >= t);
				if (thumbNumber == -1) thumbNumber = 0;
			} else {
				thumbNumber = Math.floor(cursorTime / stepInSeconds);
			}

			const thumbRow = Math.floor(thumbNumber / spriteRows);
			const thumbColumn = thumbNumber % spriteRows;
			const thumbPositionX = thumbColumn * this.thumbnailWidth;
			const thumbPositionY = thumbRow * this.thumbnailHeight;
			this.el().style.setProperty('--background-position', `-${thumbPositionX}px -${thumbPositionY}px`);
			if (thumbnailTime || thumbnailTime === 0) return;
			this.adjustContainerPosition();
		});
	}

	initOfTimelineEvents() {
		const eventsOnTimeline = this.player().getDescendant(['ControlBar', 'EventsOnTimeline']);
		if (!eventsOnTimeline) return;
		eventsOnTimeline.children().forEach((eventItem) => {
			if (!eventItem.el()) return;
			eventItem.on('mouseenter', () => {
				const timelineEventTime = Number(eventItem.getAttribute('data-time'));
				if (this.options_.type === 'images') {
					this.setThumbnailOfLive(this.images, timelineEventTime);
				} else {
					this.showThumbnailOfSprite(this.getSpriteSizes(), timelineEventTime);
				}
				this.adjustContainerPositionForEventItem(eventItem);
				this.player().addClass('vjs-timeline-preview__thumbnail-show');
			});
			eventItem.on('mouseleave', () => {
				this.player().removeClass('vjs-timeline-preview__thumbnail-show');
			});
		});
	}

	async initOfImages() {
		this.addClass('vjs-timeline-preview__thumbnail_live');

		let data;
		try {
			const resp = await fetch(this.options_.url);
			data = await resp.json();
			this.images = data.images;
		} catch(error) {
			data = {
				images: {},
				timeout: 5 // Retry timeout
			};
		}

		const setThumbnailOfLive = this.setThumbnailOfLive.bind(this, data.images, null);
		const parentEl = this.options_.progressControl.el();
		parentEl.addEventListener('mousemove', setThumbnailOfLive);
		setTimeout(() => {
			parentEl.removeEventListener('mousemove', setThumbnailOfLive);
			this.initOfImages();
		}, data.timeout * 1000);
	}

	setThumbnailOfLive(images, thumbnailTime) {
		// setTimeout нужен чтобы получить актуальное значение this.options_.timeTooltip.el().innerText из DOM
		setTimeout(() => {
			const hls = this.player().hls_;
			if (!hls.levels || !hls.levels[hls.currentLevel]) return;
			const fragments = hls.levels[hls.currentLevel].details.fragments;
			
			const [
				cursorSeconds = '0',
				cursorMinutes = '0',
				cursorHours = '0'
			] = this.options_.timeTooltip.el().innerText.split(':').reverse().map((val) => Number.parseInt(val, 10));
			const cursorTime = thumbnailTime ? thumbnailTime : (cursorHours * 3600 + cursorMinutes * 60 + cursorSeconds);

			const fragmentAtCursor = fragments.find(({ start }) => start >= cursorTime);
			if (!fragmentAtCursor) return;

			const chunkTime = this.getTimestampFromUrl(fragmentAtCursor.relurl);
			const imageTimestampList = Object.keys(images);
			const imageTimestamp = findLast(
				imageTimestampList,
				(timestamp) => chunkTime >= timestamp
			);
			const imageUrl = images[imageTimestamp] || images[imageTimestampList[0]];
			if (imageUrl) {
				this.el().style.setProperty('--background-image', `url("${imageUrl}")`);
			}
			if (thumbnailTime || thumbnailTime === 0) return;
			this.adjustContainerPosition();
		});
	}

	/**
	 * Преобразование таймстампа чанка из URL к целочисленному значению
	 *
	 * @param {String} chunkUrl - URL чанка из плейлиста
	 * @returns {Number} Значение таймстампа
	 */
	getTimestampFromUrl(chunkUrl) {
		// Извлекает из строки с URL часть вида 1693325749863.ts/1187/0, где часть после .ts не
		// обязательна
		const tsMatches = chunkUrl.match(/\/(\d+)\.ts\/*(\d+)*\/*(\d+)*/);
		// Если формат названия чанка не совпал, то возвращаем самое маленькое возможное число,
		// чтобы ни одно изображение не было выбрано
		if (!tsMatches.length) {
			return -Infinity;
		}

		// Преобразуем строковые значения к целочисленным. Получаем следующие значения:
		// 	* ts - Таймстамп чанка.
		// 	* seekFrom - Место, с которого выполнялась подрезка.
		// 	* seekTo - Место, до которого выполнялнась подрезка.
		const [ts, seekFrom, seekTo] = tsMatches.slice(1, 4).map((item) => item ? Number(item) : 0);
		// Преобразуем полученные значения к таймстампу без миллисекунд
		return Math.floor((ts + seekFrom - seekTo) / 1000);
	}

	adjustContainerPosition() {
		let left;
		let transform;
		const mouseTimeDisplayLeft = Number.parseFloat(this.options_.mouseTimeDisplay.el().style.left);
		const progressControlWidth = this.options_.progressControl.currentWidth();
		if (mouseTimeDisplayLeft < this.thumbnailWidth / 2) {
			// left
			left = 0;
			transform = 'none';
		} else if (progressControlWidth - mouseTimeDisplayLeft <= this.thumbnailWidth / 2) {
			// right
			left = `${progressControlWidth - this.thumbnailWidth / 2}px`;
			transform = 'translateX(-50%)';
		} else {
			// middle
			left = `${mouseTimeDisplayLeft}px`;
			transform = 'translateX(-50%)';
		}
		this.el().style.setProperty('--left', left);
		this.el().style.setProperty('--transform', transform);
	}

	/**
	 * @param {videojs.Component} eventItem
	 * @return {void}
	 */
	adjustContainerPositionForEventItem(eventItem) {
		const containerWidth = this.thumbnailWidth;
		const eventItemOffset = eventItem.el().offsetLeft;
		const progressControlWidth = this.options_.progressControl.currentWidth();

		let position;
		let transform;

		if (eventItemOffset + containerWidth > progressControlWidth) {
			position = progressControlWidth - containerWidth;
			transform = 'translateX(-50%)';
		} else if (eventItemOffset < containerWidth / 2) {
			position = 0;
			transform = 'none';
		} else {
			position = eventItemOffset;
			transform = 'translateX(-50%)';
		}

		this.el().style.setProperty('--left', `${position}px`);
		this.el().style.setProperty('--transform', transform);
	}

	createEl() {
		return videojs.dom.createEl('div', {
			className: 'vjs-timeline-preview__thumbnail'
		});
	}
}

videojs.registerComponent('TimelinePreviewImage', TimelinePreviewImage);
